import React, { useState, useEffect } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";

import { Link, useSearchParams } from "react-router-dom";

import axios from "../api/axios";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import NavbarGuest from "./NavbarGuest";
import Footer from "./Footer";

import useResponsiveFlags from "../hooks/useResponsiveFlags";

export default function ResetPassword() {
  const { isMobile } = useResponsiveFlags();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState(null);
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [error, setError] = useState({});
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    validateToken();
  }, []);

  const [values, setValues] = useState({
    newPassword: "",
    confirmPassword: "",
    showNewPassword: false,
    showConfirmPassword: false,
  });

  const validateToken = async () => {
    if (!token) {
      setIsTokenValid(false);
      return;
    }
    try {
      await axios.get('/sanctum/csrf-cookie');
      const response = await axios.post('/validate-reset-token', { token });
      // ✅ Success case
      if (response.data.valid) {
        setEmail(response.data.email);
        setIsTokenValid(true);
        setError({});
      }

    } catch (error) {
      const res = error.response;
      if (res?.data?.type === "expired") {
        setIsTokenValid("expired");
      } else if (res?.data?.type === "invalid") {
        setIsTokenValid(false);
      } else if (!res) {
        setIsTokenValid(false);
        setError({ token: "Network error. Please check your connection and try again." });
      }

      // 🔥 3. Server error (500, etc.)
      else {
        setIsTokenValid(false);
        setError({ token: "Something went wrong. Please try again later." });
      }
    }
  };

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickShowPassword = (field) => {
    setValues({ ...values, [field]: !values[field] });
  };

  const validatePassword = () => {
    let tempErrors = {};
  
    const password = values.newPassword;
  
    if (password.length < 8) {
      tempErrors.newPassword = "Password must be at least 8 characters.";
    } else if (!/[A-Z]/.test(password)) {
      tempErrors.newPassword = "Password must include an uppercase letter.";
    } else if (!/[a-z]/.test(password)) {
      tempErrors.newPassword = "Password must include a lowercase letter.";
    } else if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
      tempErrors.newPassword = "Password must include a special character.";
    }
  
    if (password !== values.confirmPassword) {
      tempErrors.confirmPassword = "Passwords do not match.";
    }
  
    setError(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validatePassword()) return;
  
    setSubmitted(true);
    setError({});
  
    try {
      const response = await axios.post('/reset-password', {
        email,
        token,
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
      });
  
      const data = await response.data;
  
      if (data.success) {
        handleClear();
        setChanged(true);
      }
  
    } catch (error) {
      const res = error.response;
  
      // Validation errors (422)
      if (res?.status === 422) {
        // Laravel default structure
        if (res.data.errors) {
          const fieldErrors = res.data.errors;
  
          setError({
            newPassword: fieldErrors.password?.[0],
            confirmPassword: fieldErrors.password_confirmation?.[0],
          });
        }
        // Your custom structure
        else if (res.data.message) {
          setError({ general: res.data.message });
        }
      }
  
      // Token / business logic errors (400)
      else if (res?.status === 400) {
        setError({ general: res.data.message });
      }
  
      // Network error
      else if (!res) {
        setError({
          general: "Network error. Please check your connection.",
        });
      }
  
      // Unexpected server error
      else {
        setError({
          general: "Something went wrong. Please try again later.",
        });
      }
  
    } finally {
      setSubmitted(false);
    }
  };
  const handleClear = () => {
    setError({});
    setValues({
      newPassword: "",
      confirmPassword: "",
      showNewPassword: false,
      showConfirmPassword: false,
    });
  };
  return (
    <>
      <section className="fullScreenLayout">
        <NavbarGuest />
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ padding: "20px", flex: "1" }}
        >
          <div className="p-4">
            {isTokenValid === null &&
              <p className="text-center para">Validating Token...</p>
            }
            {isTokenValid === "expired" &&
              <>
                <p className="text-center login">Expired Token</p>
                <p className={`text-center para error-text`} >
                  This reset link is no longer valid. Please request a new one.
                </p>
                <p className="text-center mt-2">
                  <Link to="/forgotpassword">Back to Forgot Password.</Link>
                </p>
              </>
            }
            {isTokenValid === false &&
              <>
                <p className="text-center login">Invalid Token</p>
                <p className={`text-center para error-text`} >
                  {error.token ? error.token :
                    "This reset link looks broken. Please check the link again or request a new one."}
                </p>
                <p className="text-center mt-2">
                  <Link to="/forgotpassword">Back to Forgot Password.</Link>
                </p>
              </>
            }
            {changed === true &&
              <>
                <p className="text-center login">Reset Password</p>
                <p className={`text-center para success-text`} >
                  Password has been changed successfully.
                </p>
                <p className="text-center mt-2">
                  click here for <Link to="/login">login</Link>
                </p>
              </>
            }
            {(isTokenValid === true && changed=== false) &&
              <>
                <h2 className="text-center login">Reset Password</h2>
                <div style={{ maxWidth: "40rem", padding: isMobile ? "" : "20px" }}>
                  {error.general && (
                    <Typography
                      color="error"
                      sx={{ marginLeft: isMobile ? "" : "12.5rem" }}
                    >
                      {error.general}
                    </Typography>
                  )}
                  <div className={`${isMobile ? "" : "d-flex"}`}>
                    <p className="change-password-fields" >
                      <b>New Password</b>
                    </p>
                    <TextField
                      label="New Password"
                      type={values.showNewPassword ? "text" : "password"}
                      fullWidth
                      margin="normal"
                      value={values.newPassword}
                      error={Boolean(error.newPassword || error.confirmPassword)}
                      onChange={handleChange("newPassword")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleClickShowPassword("showNewPassword")}
                            >
                              {values.showNewPassword ? (
                                <VisibilityOffOutlinedIcon />
                              ) : (
                                <VisibilityOutlinedIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={
                        <>
                          {error.newPassword && (
                            <span style={{ marginLeft: "-14px" }}>{error.newPassword}</span>
                          )}
                          <Typography
                            variant="body2"
                            className="review-text"
                            style={{
                              marginBottom: "0",
                            }}
                          >
                            <span style={{ marginLeft: "-14px" }}>
                              Password must:
                              <ul style={{ paddingLeft: "1rem", marginBottom: "0" }}>

                                <li>Be at least 8 characters</li>
                                <li>Include both uppercase and lowercase letters</li>
                                <li>
                                  Include at least one special character (e.g., ! @ # $)
                                </li>
                              </ul>
                            </span>
                          </Typography>
                        </>
                      }
                    />
                  </div>

                  <div className={`${isMobile ? "" : "d-flex"}`}>
                    <p className="change-password-fields" >
                      <b>Confirm New Password</b>
                    </p>
                    <TextField
                      label="Confirm New Password"
                      type={values.showConfirmPassword ? "text" : "password"}
                      fullWidth
                      margin="normal"
                      value={values.confirmPassword}
                      error={Boolean(error.confirmPassword)}
                      helperText={
                        error.confirmPassword && (
                          <span style={{ marginLeft: "-14px" }}>{error.confirmPassword}</span>

                        )
                      }
                      onChange={handleChange("confirmPassword")}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                handleClickShowPassword("showConfirmPassword")
                              }
                            >
                              {values.showConfirmPassword ? (
                                <VisibilityOffOutlinedIcon />
                              ) : (
                                <VisibilityOutlinedIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>

                  <div
                    className={`${isMobile ? "" : "d-flex"}`}
                    style={{ marginTop: "20px" }}
                  >
                    {!isMobile && <p style={{ width: "12.4rem" }}></p>}
                    <button
                      className="btn cancel-btn"
                      style={{ ...(isMobile && { width: "100%", marginBottom: "1rem" }) }}
                      onClick={handleClear}
                      disabled={submitted}>
                      Clear
                    </button>
                    <button
                      className="continue-btn"
                      style={{ ...(isMobile && { width: "100%" }), padding: "10px 25px" }}
                      onClick={handleSubmit}
                      disabled={submitted}
                    >
                      {submitted ? "Processing..." : "Change Password"}
                    </button>
                  </div>
                </div></>}
          </div>
        </div>
        <Footer />
      </section>

    </>
  );
};